import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import './styles.scss'

// Portapapeles entre el equipo del estudiante y la máquina remota.
// - Automático (HTTPS): al enfocar la sesión se envía el portapapeles local a la máquina,
//   y lo que se copia en la máquina se escribe en el portapapeles local.
// - Manual (panel): respaldo cuando el navegador no da permiso o la página no es HTTPS.

// Tope de texto que se envía a la máquina (evita pegar volcados enormes por error)
export const MAX_CLIPBOARD_CHARS = 256 * 1024

export const clampClipboard = (text) =>
  text.length > MAX_CLIPBOARD_CHARS ? text.slice(0, MAX_CLIPBOARD_CHARS) : text

// La Clipboard API solo existe en contextos seguros (HTTPS o localhost)
export const clipboardApiAvailable = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.clipboard &&
  typeof navigator.clipboard.readText === 'function' &&
  typeof navigator.clipboard.writeText === 'function' &&
  window.isSecureContext !== false

// Copia texto al portapapeles local. Usa la Clipboard API y, si no está, execCommand
// (funciona también en HTTP porque se llama desde un clic del usuario).
const copyToLocal = async (text, textareaEl) => {
  if (clipboardApiAvailable()) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (e) {
      /* sin permiso: probar con execCommand */
    }
  }
  if (textareaEl) {
    textareaEl.focus()
    textareaEl.select()
    try {
      return document.execCommand('copy')
    } catch (e) {
      return false
    }
  }
  return false
}

// Sincronización automática del portapapeles de la VM activa
export const useClipboardSync = ({ vmId, active, connected, remoteText, send }) => {
  const lastSent = useRef({}) // Último texto enviado/recibido por vmId
  const lastRemote = useRef({ vmId, text: remoteText })
  const sendRef = useRef(send)
  sendRef.current = send

  // Local -> máquina: al activar la sesión y cada vez que se vuelve a la ventana
  useEffect(() => {
    if (!active || !connected || !clipboardApiAvailable()) return undefined
    let cancelled = false
    // Sesión nueva o recién activada: su portapapeles puede estar vacío, reenviar
    delete lastSent.current[vmId]

    const run = async () => {
      let text
      try {
        text = await navigator.clipboard.readText()
      } catch (e) {
        return // permiso denegado o documento sin foco
      }
      if (cancelled) return
      text = clampClipboard(text || '')
      if (!text || text === lastSent.current[vmId]) return
      if (sendRef.current(text)) lastSent.current[vmId] = text
    }

    run()
    window.addEventListener('focus', run)
    return () => {
      cancelled = true
      window.removeEventListener('focus', run)
    }
  }, [vmId, active, connected])

  // Máquina -> local: lo que se copia en la máquina queda en el portapapeles local.
  // Solo cuando la misma máquina copia algo nuevo; cambiar de pestaña no pisa el portapapeles local.
  useEffect(() => {
    const prev = lastRemote.current
    lastRemote.current = { vmId, text: remoteText }
    if (!remoteText || prev.vmId !== vmId || prev.text === remoteText) return
    lastSent.current[vmId] = remoteText // evita devolverle a la máquina su propio texto
    if (clipboardApiAvailable()) {
      navigator.clipboard.writeText(remoteText).catch(() => {
        /* sin foco o sin permiso: queda disponible en el panel */
      })
    }
  }, [vmId, remoteText])
}

// Panel manual "Portapapeles"
const ClipboardPanel = ({ displayName, connected, remoteText, send, onClose }) => {
  const [text, setText] = useState('')
  const [msg, setMsg] = useState(null)
  const remoteRef = useRef(null)
  const api = clipboardApiAvailable()

  const handleSend = () => {
    if (send(clampClipboard(text))) {
      setMsg('Texto enviado. Haz clic en la máquina y pega con Ctrl+V.')
    } else {
      setMsg('La máquina no está conectada. Conéctala e inténtalo de nuevo.')
    }
  }

  const handleCopy = async () => {
    const ok = await copyToLocal(remoteText, remoteRef.current)
    setMsg(ok ? 'Copiado en tu equipo. Ya puedes pegarlo con Ctrl+V.' : 'No se pudo copiar. Selecciona el texto y usa Ctrl+C.')
  }

  return (
    <div className="clipboard-panel" role="dialog" aria-label={`Portapapeles de ${displayName}`}>
      <div className="clipboard-panel-header">
        <strong>Portapapeles</strong>
        <button type="button" className="clipboard-panel-close" onClick={onClose} aria-label="Cerrar portapapeles">
          ×
        </button>
      </div>

      {!api && (
        <p className="clipboard-panel-hint">
          Tu navegador no permite el portapapeles automático. Usa este panel para copiar y pegar.
        </p>
      )}

      <label className="clipboard-panel-field">
        <span>Pega aquí (Ctrl+V) el texto que quieres llevar a la máquina</span>
        <textarea
          value={text}
          maxLength={MAX_CLIPBOARD_CHARS}
          onChange={(e) => setText(e.target.value)}
          rows={4}
        />
      </label>
      <Button
        type="button"
        icon="pi pi-upload"
        className="p-button-success"
        label="Enviar a la máquina"
        disabled={!connected || !text}
        onClick={handleSend}
      />

      <label className="clipboard-panel-field">
        <span>Último texto copiado en la máquina</span>
        <textarea ref={remoteRef} readOnly value={remoteText} rows={4} placeholder="Copia algo dentro de la máquina (Ctrl+C) y aparecerá aquí" />
      </label>
      <Button
        type="button"
        icon="pi pi-copy"
        className="p-button-secondary"
        label="Copiar a mi equipo"
        disabled={!remoteText}
        onClick={handleCopy}
      />

      {msg && (
        <p className="clipboard-panel-status" role="status">
          {msg}
        </p>
      )}
    </div>
  )
}

export default ClipboardPanel
